<script setup lang="ts">
//#region imports
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { omit } from 'lodash-es';
import { useApi } from '@/api';
import { useDetectPermissions } from '@/composables/useDetectPermissions';
import { useFormatters } from '@/composables/useFormatters';
import { usePermissions } from '@/composables/usePermissions';
import { useTablePaginator, useTableSearch, useTableSort } from '@/composables/useTableLS';
import { useUpdateUrl } from '@/composables/useUpdateUrl';
import { usePopupManager } from '@/components/PopupManager.vue';
import { useLs } from '@/setup/vueLs';
import UiPagination from '@/components/UIElements/UiPagination.vue';
import type { UiTableNewField, UiTableNewSortEmit } from '@/types/table';
import type { GetPaymentsHistoryByIdResponse, GetPaymentsHistoryCurrenciesResponse, GetPaymentsHistoryData } from '@/types/openapi-ts';
import type { SettingsPageQuery } from '@/types/core';
//#endregion

//#region types
type ExternalPaymentRow = GetPaymentsHistoryByIdResponse['payload'];
type ExternalPaymentsQuery = SettingsPageQuery<GetPaymentsHistoryData['query']>;
type HistoryCurrency = GetPaymentsHistoryCurrenciesResponse['payload'][number];
//#endregion

//#region const
const LS_PATH = 'ExternalPayments';

const sizes = {
  1080: 20,
  1440: 30,
};

const fields: UiTableNewField<ExternalPaymentRow>[] = [
  {
    name: 'incrementing_id',
    align: 'left',
    sortable: 'custom',
    width: '70',
    fixed: 'left',
  },
  {
    name: 'affiliate_email',
    align: 'left',
    minWidth: '200',
    sortable: 'custom',
    link: (row) => `/affiliates/${row.affiliate_id}/info`,
  },
  {
    name: 'team_name',
    width: '140',
  },
  {
    name: 'approved_by_admin_name',
    align: 'left',
    minWidth: '180',
    sortable: 'custom',
  },
  {
    name: 'amount',
    align: 'right',
    width: '100',
    class: 'bold',
    sortable: 'custom',
    computedValue: (row) => formatCurrencyValue(row.currency_code, row.amount),
    computedClass: (_value, row) => (row.amount < 0 ? 'negative' : ''),
  },
  {
    name: 'available_balance',
    align: 'right',
    width: '100',
    sortable: 'custom',
    computedValue: (row) => formatCurrencyValue(row.balance_currency_code, row.available_balance),
  },
  {
    name: 'hold_balance',
    align: 'right',
    width: '100',
    sortable: 'custom',
    computedValue: (row) => formatCurrencyValue(row.balance_currency_code, row.hold_balance),
  },
  {
    name: 'payout_status',
    width: '120',
    type: 'status',
    status: (value) => String(value || ''),
    statusText: (value) => t(`payments.page_title.payments_payout.${value}`),
  },
  {
    name: 'created_at',
    width: '140',
    align: 'left',
    sortable: 'custom',
    format: 'date-time',
  },
  {
    name: 'paid_at',
    format: 'date-time',
    width: '140',
    align: 'left',
    sortable: 'custom',
  },
];
//#endregion

//#region composables
const api = useApi();
const route = useRoute();
const ls = useLs();
const popupManager = usePopupManager();
const updateUrl = useUpdateUrl();
const { t } = useI18n();
const { formatCurrencyValue } = useFormatters();
const { checkRequest, checkRoute } = useDetectPermissions();
const { hasPermission } = usePermissions();

const { limit, count, page, setPage } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 15,
});

const { sortProp, sortOrder, optionsSort } = useTableSort({
  initialOptions: {
    sort_column: 'incrementing_id',
    sort_dir: 'desc',
  },
  lsPath: LS_PATH,
  fields,
});

const { searchQuery } = useTableSearch();
//#endregion

//#region state
const isDataLoading = ref(false);
const isFirstLoading = ref(true);
const currencyCode = ref('');
const historyCurrencies = ref<HistoryCurrency[]>([]);
const tableData = ref<ExternalPaymentRow[]>([]);
//#endregion

//#region computed
const allCurrencies = computed(() => [
  { name: t('settings.currencies.allCurrencies'), code: '' },
  ...historyCurrencies.value,
]);
//#endregion

//#region methods
function resolveCurrencyCode(code?: string | null) {
  if (code && historyCurrencies.value.some((currency) => currency.code === code)) {
    return code;
  }

  return '';
}

async function initData() {
  if (checkRequest('payments_view')) {
    const response = await api.getPaymentsHistoryCurrencies();
    historyCurrencies.value = response.data.payload || [];

    const urlData = updateUrl.getParseParams(route.hash);

    if (urlData && Object.keys(urlData).length) {
      setPage(urlData);
      if (urlData.search) {
        searchQuery.value = String(urlData.search);
      }
      currencyCode.value = resolveCurrencyCode(urlData.currency_code && String(urlData.currency_code));
    } else {
      currencyCode.value = resolveCurrencyCode(ls.get(`${LS_PATH}/currency_code`));
    }

    nextTick(() => {
      fetchData();
    });
  }

  checkRoute('payments_view');
}

function buildQuery() {
  return {
    ...optionsSort.value,
    limit: limit.value,
    offset: limit.value * page.value - limit.value,
    search: searchQuery.value || null,
    type: 'external',
    currency_code: currencyCode.value,
  } as GetPaymentsHistoryData['query'];
}

function updatePageUrl() {
  const params: ExternalPaymentsQuery = omit(buildQuery(), 'offset');
  params.page = page.value;
  updateUrl.updateGetParams(params);
}

async function fetchData() {
  if (!isFirstLoading.value) {
    isDataLoading.value = true;
  }

  updatePageUrl();

  try {
    const response = await api.getPaymentsHistory(buildQuery());
    tableData.value = (response.data.payload?.data || []) as ExternalPaymentRow[];
    count.value = response.data.misc?.count || 0;
  } finally {
    isDataLoading.value = false;
    isFirstLoading.value = false;
  }
}

function pageChange(pageVal: number, size: number) {
  if (size !== limit.value) {
    limit.value = size;
    page.value = 1;
  } else {
    page.value = pageVal;
  }
  fetchData();
}

function handleSort({ prop, order }: UiTableNewSortEmit) {
  if (!order) {
    return;
  }
  sortProp.value = prop;
  sortOrder.value = order;
  nextTick(() => {
    fetchData();
  });
}

function handleRefresh() {
  page.value = 1;
  fetchData();
}

function changeCurrency(value: string) {
  currencyCode.value = value;
  ls.set(`${LS_PATH}/currency_code`, value);
  fetchData();
}

function canDecline(row: ExternalPaymentRow) {
  return hasPermission('payments_external_payouts_actions')
    && row.payout_status !== 'cancelled'
    && !row.reversal_of_payment_id;
}

function handleDecline(row: ExternalPaymentRow) {
  popupManager.declineExternalPayment.open({
    data: {
      paymentId: row.id,
      affiliateEmail: row.affiliate_email,
    },
    listeners: {
      success: () => {
        fetchData();
      },
    },
  });
}
//#endregion

//#region hooks
initData();
//#endregion
</script>

<template>
  <div class="external-payments">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('payments.page_title.external_payments') }}
      </template>
      <template v-slot:left-slot>
        <UiInput
          v-model.trim="searchQuery"
          :width="180"
          is-search
          @search="handleRefresh"
        />
      </template>
      <template v-slot:right-slot>
        <UiCurrencyPicker
          :model-value="currencyCode"
          :currencies="allCurrencies"
          :disabled="isDataLoading"
          @update:model-value="changeCurrency"
        />
        <UiPagination
          :page="page"
          :page-size="limit"
          :count="count"
          show-size-select
          @page-change="pageChange"
        />
      </template>
    </UiToolbar>
    <UiTableNew
      v-loading="isDataLoading"
      :fields="fields"
      :data="tableData"
      :rows-count="limit"
      :currency="currencyCode"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      lazy-loading
      is-static
      i18n-path="payments.history"
      @sort="handleSort"
    >
      <template v-slot:append>
        <ElTableColumn
          :resizable="false"
          width="76"
          fixed="right"
          align="center"
        >
          <template v-slot:header>
            {{ $t('settings.tokens.actions') }}
          </template>
          <template v-slot="scope">
            <ActionIcon
              icon="eye"
              :tooltip="$t('payments.history.info')"
              :router-to="`/payments/info/${scope.row.id}?from=external_payments`"
            />
            <ActionIcon
              icon="times"
              color="var(--text-negative)"
              :disabled="!canDecline(scope.row)"
              :tooltip="$t('payments.history.decline')"
              @click="handleDecline(scope.row)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>
